'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Eye, EyeOff, AlertCircle, Lock } from 'lucide-react'
import api from '@/lib/api'

interface ForcedPasswordResetModalProps {
  open: boolean
  onResetComplete?: () => void
}

export default function ForcedPasswordResetModal({ open, onResetComplete }: ForcedPasswordResetModalProps) {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [validationErrors, setValidationErrors] = useState<string[]>([])

  if (!open) return null

  const validatePassword = (password: string): string[] => {
    const errors: string[] = []

    if (!password) {
      errors.push('Password is required')
    } else {
      if (password.length < 8) {
        errors.push('Minimum 8 characters')
      }
      if (!/[A-Z]/.test(password)) {
        errors.push('At least one uppercase letter')
      }
      if (!/[a-z]/.test(password)) {
        errors.push('At least one lowercase letter')
      }
      if (!/[0-9]/.test(password)) {
        errors.push('At least one number')
      }
    }

    return errors
  }

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value
    setNewPassword(password)
    if (password) {
      setValidationErrors(validatePassword(password))
    } else {
      setValidationErrors([])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate inputs
    if (!newPassword || !confirmPassword) {
      setError('Both password fields are required')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    const errors = validatePassword(newPassword)
    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    setIsLoading(true)

    try {
      await api.patch('/api/auth/password/force-reset', {
        new_password: newPassword,
        confirm_password: confirmPassword,
      })

      setNewPassword('')
      setConfirmPassword('')
      setValidationErrors([])

      if (onResetComplete) {
        onResetComplete()
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to reset password. Please try again.'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="max-w-md w-full mx-4">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Lock className="h-5 w-5" />
            <span>Set Your Password</span>
          </CardTitle>
          <CardDescription>
            This is your first login. You must set a new password before you can access the system.
          </CardDescription>
        </CardHeader>

        <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert className="border-red-500 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-600">Error</AlertTitle>
              <AlertDescription className="text-red-600">{error}</AlertDescription>
            </Alert>
          )}

          {/* Info Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Password Requirements</AlertTitle>
            <AlertDescription className="text-sm">
              At least 8 characters with uppercase, lowercase, and number
            </AlertDescription>
          </Alert>

          {/* New Password Field */}
          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handleNewPasswordChange}
                placeholder="Enter new password"
                disabled={isLoading}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="space-y-1">
                {validationErrors.map((err, index) => (
                  <p key={index} className="text-xs text-red-600 flex items-center">
                    <span className="mr-2">•</span>
                    {err}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                disabled={isLoading}
                className={`pr-10 ${newPassword && confirmPassword && newPassword !== confirmPassword ? 'border-red-500' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Password Mismatch Warning */}
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-600 flex items-center">
                <span className="mr-2">•</span>
                Passwords do not match
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading || validationErrors.length > 0 || !newPassword || !confirmPassword}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting Password...
              </>
            ) : (
              'Set Password & Continue'
            )}
          </Button>
        </form>
        </CardContent>
      </Card>
    </div>
  )
}
